import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a UK invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A standard UK invoice must include: your business name and address; the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount charged; and payment terms. If you are VAT-registered, it must also include your VAT number, the VAT rate, VAT amount, and the gross total.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to charge VAT on UK invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You must charge VAT only if your business is VAT-registered. VAT registration is required when your taxable turnover exceeds £90,000 in a 12-month period. Below this threshold, you can issue standard invoices without VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a UK invoice for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for UK businesses and freelancers. Create VAT invoices or standard invoices, download a professional PDF, and send to clients. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between a VAT invoice and a standard invoice in the UK?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A VAT invoice is issued by a VAT-registered business and must show the VAT number, the net amount, the VAT rate and amount, and the gross total. A standard invoice is issued by non-VAT-registered businesses or sole traders and does not include VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do self-employed people in the UK invoice clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Self-employed people in the UK issue invoices like any business. Include your name, address, a unique invoice number, the date, a description of work, your rate, and the total amount. If VAT-registered, add your VAT number and the tax breakdown. Invilic provides free invoice templates for both VAT and non-VAT self-employed invoices.',
      },
    },
  ],
};

export default function InvoiceGeneratorUKPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator UK: Free VAT & Standard Invoice Maker | Invilic"
        description="Free invoice generator for UK businesses, freelancers, and self-employed. Create VAT invoices with HMRC-required fields or standard invoices. Download PDF free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-uk"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇬🇧 United Kingdom
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator UK
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for UK businesses, freelancers, and sole traders. Create VAT invoices with all HMRC-required fields, or standard invoices if you are not VAT-registered. Download a professional PDF in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/vat"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200"
            >
              Create VAT Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200"
            >
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Which invoice */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Percent,
                title: 'VAT Invoice',
                subtitle: 'For VAT-registered businesses',
                features: ['VAT number on invoice', 'Net amount + VAT rate', 'Auto VAT calculation', 'Gross total', 'Reverse charge option'],
                to: '/create/vat',
                color: 'orange',
                btnClass: 'bg-orange-500 hover:bg-orange-600',
              },
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For non-VAT sole traders & freelancers',
                features: ['No VAT required', 'Custom line items', 'Logo upload', 'Payment terms', 'Bank details'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For UK businesses billing overseas',
                features: ['Multi-currency billing', 'USD, EUR, AUD support', 'SWIFT / IBAN fields', 'Export invoice format'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* VAT guide */}
        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
            UK VAT Invoice vs Standard Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-orange-600 dark:text-orange-400 mb-3">Use a VAT Invoice if:</div>
              {[
                'Your taxable turnover exceeds £90,000',
                'You are registered for VAT with HMRC',
                'Your client is VAT-registered and needs to reclaim VAT',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-600 dark:text-blue-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your turnover is below the £90,000 VAT threshold',
                'You are a freelancer, sole trader, or contractor not registered for VAT',
                'Your client does not need a VAT invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            UK Invoice Generator: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your UK Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for UK businesses in under 2 minutes. No account required.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
